import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  MessagesSquare, ChevronLeft, X, Check, Ban, Trash2, Scissors, Wrench, Package,
  Eye, Send, Plus, Clock, CheckCheck, ShoppingCart, Search, Paperclip,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MediaUploader from "@/components/MediaUploader";

const RECIPIENTS = [
  { key: "reparto_tecnico", label: "Ufficio Tecnico" },
  { key: "warehouse", label: "Magazziniere" },
];
const recLabel = (k) => (RECIPIENTS.find((r) => r.key === k) || {}).label || k;

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "ora";
  if (s < 3600) return `${Math.floor(s / 60)} min fa`;
  if (s < 86400) return `${Math.floor(s / 3600)} h fa`;
  return `${Math.floor(s / 86400)} gg fa`;
}

const STATUS = {
  inviata: { label: "Inviata", cls: "bg-[#eef1f6] text-[#5b6b82]", icon: Clock },
  visualizzata: { label: "Visualizzata", cls: "bg-[#eef2fb] text-[#2c4a89]", icon: Eye },
  accettata: { label: "Accettata", cls: "bg-[#e8f2e9] text-[#1c7a44]", icon: Check },
  completata: { label: "Completata", cls: "bg-[#e8f2e9] text-[#1c7a44]", icon: CheckCheck },
  declinata: { label: "Declinata", cls: "bg-[#fbeaec] text-[#9c1e2b]", icon: Ban },
};

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.inviata;
  const Icon = s.icon;
  return (
    <span data-testid={`req-status-${status}`} className={`inline-flex items-center gap-1 text-[10px] font-700 uppercase tracking-wide px-1.5 py-0.5 rounded-[3px] ${s.cls}`}>
      <Icon size={11} /> {s.label}
    </span>
  );
}

const emptyForm = {
  kind: "taglio", recipient: "reparto_tecnico", machine_type: "", code: "", note: "", quantity: 1, thickness: "",
  attachments: [], source: "magazzino", item_name: "", item_code: "", component_key: "", ref_id: "", unit: "pz", price: "", supplier: "",
};

export default function RequestChat() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("abrams_reqchat_collapsed") === "1");
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [meta, setMeta] = useState({ machine_types: [] });
  const [tab, setTab] = useState("inviate");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [declineFor, setDeclineFor] = useState(null);
  const [declineNote, setDeclineNote] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [machineFilter, setMachineFilter] = useState("");
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [stock, setStock] = useState([]);
  const [raw, setRaw] = useState([]);
  const [itemQuery, setItemQuery] = useState("");
  const [myRequests, setMyRequests] = useState([]);
  const panelRef = useRef(null);
  const bubbleRef = useRef(null);
  const DEFAULT_POS = { right: 20, bottom: 24 };
  const [pos, setPos] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem("abrams_reqchat_pos")); if (s && typeof s.right === "number" && typeof s.bottom === "number") return s; } catch { /* noop */ }
    return DEFAULT_POS;
  });
  const drag = useRef({ active: false, startX: 0, startY: 0, baseRight: 0, baseBottom: 0, moved: false, collapse: false, last: null });

  const isRecipient = user && (RECIPIENTS.some((r) => r.key === user.role) || user.role === "admin");
  const isSales = user && user.role === "sales";
  const setForm2 = (patch) => setForm((f) => ({ ...f, ...patch }));
  const pickerResults = (() => {
    const list = form.source === "materia_prima" ? raw : stock;
    const nq = itemQuery.trim().toLowerCase();
    if (!nq) return [];
    return list.filter((s) => `${s.name || ""} ${s.code || ""}`.toLowerCase().includes(nq)).slice(0, 12);
  })();
  const selectItem = (s) => {
    if (form.source === "materia_prima") setForm2({ item_name: s.name, item_code: s.code || "", ref_id: s.id, component_key: "", unit: s.unit || "pz" });
    else setForm2({ item_name: s.name, item_code: s.code || "", component_key: s.key || "", ref_id: "", unit: s.unit || "pz" });
    setItemQuery("");
  };

  const loadCount = useCallback(async () => {
    try { const { data } = await api.get("/part-requests/unread-count"); setUnread(data.count || 0); } catch { /* noop */ }
  }, []);
  const loadItems = useCallback(async () => {
    try { const { data } = await api.get("/part-requests"); setItems(data.items || []); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    setTab(isRecipient ? "ricevute" : "inviate");
  }, [isRecipient]);

  useEffect(() => {
    loadCount();
    const id = setInterval(loadCount, 20000);
    return () => clearInterval(id);
  }, [loadCount]);

  useEffect(() => {
    if (!open) return;
    loadItems();
    loadCount();
    api.get("/part-requests/meta").then((r) => setMeta(r.data)).catch(() => {});
    if (!isSales && stock.length === 0) api.get("/warehouse/stock").then((r) => setStock(r.data || [])).catch(() => {});
    if (!isSales && raw.length === 0) api.get("/raw-materials").then((r) => setRaw(r.data || [])).catch(() => {});
    const id = setInterval(loadItems, 12000);
    return () => clearInterval(id);
  }, [open, loadItems, loadCount, isSales]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMyRequests = useCallback(() => {
    if (isSales) return;
    api.get("/purchasing/my-requests").then((r) => setMyRequests(r.data || [])).catch(() => {});
  }, [isSales]);

  useEffect(() => {
    if (open && tab === "acquisti") loadMyRequests();
  }, [open, tab, loadMyRequests]);

  useEffect(() => {
    const onClick = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      if (bubbleRef.current && bubbleRef.current.contains(e.target)) return;
      setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const doCollapse = (v) => { setCollapsed(v); localStorage.setItem("abrams_reqchat_collapsed", v ? "1" : "0"); if (v) setOpen(false); };

  // drag bubble to reposition freely (persisted); tap to open; push hard past right edge to collapse
  const onPointerDown = (e) => {
    drag.current = { active: true, startX: e.clientX, startY: e.clientY, baseRight: pos.right, baseBottom: pos.bottom, moved: false, collapse: false, last: pos };
    try { e.target.setPointerCapture(e.pointerId); } catch { /* noop */ }
  };
  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    const dy = e.clientY - drag.current.startY;
    if (Math.abs(dx) + Math.abs(dy) > 6) drag.current.moved = true;
    const rawRight = drag.current.baseRight - dx;
    const newRight = Math.max(8, Math.min(window.innerWidth - 64, rawRight));
    const newBottom = Math.max(8, Math.min(window.innerHeight - 64, drag.current.baseBottom - dy));
    drag.current.collapse = rawRight < -36;
    const next = { right: newRight, bottom: newBottom };
    drag.current.last = next;
    setPos(next);
  };
  const onPointerUp = () => {
    if (!drag.current.active) return;
    const { moved, collapse, last } = drag.current;
    drag.current.active = false;
    if (collapse) { doCollapse(true); return; }
    if (!moved) { setOpen((o) => !o); return; }
    if (last) localStorage.setItem("abrams_reqchat_pos", JSON.stringify(last));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      if (form.kind === "acquisto") {
        if (!(form.item_name || "").trim()) { toast.error("Seleziona o descrivi l'articolo"); setSending(false); return; }
        if (!(parseFloat(form.quantity) > 0)) { toast.error("Indica la quantità"); setSending(false); return; }
        await api.post("/purchasing/requests", {
          item: {
            name: form.item_name.trim(), code: form.item_code || "",
            section: form.source === "materia_prima" ? "materia_prima" : "magazzino",
            component_key: form.source === "magazzino" ? (form.component_key || "") : "",
            ref_id: form.source === "materia_prima" ? (form.ref_id || "") : "",
            is_new: form.source === "nuovo", unit: form.unit || "pz",
            qty_ordered: parseFloat(form.quantity) || 0, unit_price_agreed: parseFloat(form.price) || 0,
          },
          supplier_name: form.supplier || "", notes: form.note || "", attachments: form.attachments || [],
        });
        toast.success("Richiesta di acquisto inviata ad Acquisti");
        setForm(emptyForm); setItemQuery(""); setShowForm(false); setTab("acquisti"); loadMyRequests();
        setSending(false); return;
      }
      if (!form.machine_type) { toast.error("Seleziona la tipologia macchina"); setSending(false); return; }
      await api.post("/part-requests", {
        kind: form.kind, recipient: form.recipient, machine_type: form.machine_type,
        code: form.code, note: form.note, thickness: form.kind === "richiesta" ? "" : form.thickness,
        quantity: (form.kind === "taglio" || form.kind === "richiesta") ? (parseFloat(form.quantity) || 0) : 0,
        attachments: form.attachments || [],
      });
      toast.success("Richiesta inviata");
      setForm(emptyForm); setItemQuery(""); setShowForm(false); setTab("inviate");
      loadItems(); loadCount();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore invio richiesta"); }
    finally { setSending(false); }
  };

  const respond = async (id, action, note = "") => {
    try {
      await api.patch(`/part-requests/${id}/respond`, { action, response_note: note });
      toast.success(action === "completata" ? "Richiesta completata" : action === "accettata" ? "Richiesta accettata" : "Richiesta declinata");
      setDeclineFor(null); setDeclineNote("");
      loadItems(); loadCount();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const bulkComplete = async () => {
    if (selected.length === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/part-requests/bulk-respond", { ids: selected, action: "completata" });
      toast.success(`${data.count} richieste completate`);
      setSelected([]); loadItems(); loadCount();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setBulkBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa richiesta?")) return;
    try { await api.delete(`/part-requests/${id}`); loadItems(); loadCount(); } catch { toast.error("Errore eliminazione"); }
  };

  if (!user) return null;

  const myId = user.id || user._id;
  const received = items.filter((it) => it.recipient === user.role || user.role === "admin");
  const sent = items.filter((it) => it.created_by_id === myId);
  const baseList = tab === "ricevute" ? received : sent;
  const list = baseList
    .filter((it) => (!kindFilter || it.kind === kindFilter) && (!machineFilter || it.machine_type === machineFilter)
      && (!onlyOpen || ["inviata", "visualizzata"].includes(it.status)))
    .sort((a, b) => onlyOpen ? (new Date(a.created_at || 0) - new Date(b.created_at || 0)) : 0);
  const selectable = list.filter((it) => tab === "ricevute" && ["inviata", "visualizzata"].includes(it.status));
  const selInList = selected.filter((id) => selectable.some((it) => it.id === id));

  if (collapsed) {
    return (
      <button data-testid="reqchat-reopen" onClick={() => doCollapse(false)}
        className="fixed right-0 bottom-28 z-[75] bg-[#33559d] text-white rounded-l-full pl-2 pr-1.5 py-3 shadow-lg hover:bg-[#2c4a89] transition-colors flex items-center gap-1">
        <ChevronLeft size={18} />
        {unread > 0 && <span className="min-w-[16px] h-4 px-1 rounded-full bg-[#9c1e2b] text-white text-[10px] font-700 flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}
      </button>
    );
  }

  return (
    <>
      {/* Bubble */}
      <button data-testid="reqchat-bubble" ref={bubbleRef}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
        style={{ touchAction: "none", right: `${pos.right}px`, bottom: `calc(${pos.bottom}px + env(safe-area-inset-bottom))` }}
        title="Richieste (trascina per spostare)"
        className="fixed z-[75] w-14 h-14 rounded-full bg-[#33559d] text-white shadow-xl hover:bg-[#2c4a89] active:scale-95 transition-colors flex items-center justify-center cursor-grab active:cursor-grabbing">
        <MessagesSquare size={24} />
        {unread > 0 && (
          <span data-testid="reqchat-badge" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-[#9c1e2b] text-white text-[11px] font-700 flex items-center justify-center border-2 border-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div data-testid="reqchat-panel" ref={panelRef}
          className="fixed z-[76] left-2 right-2 top-2 h-[calc(100dvh-1rem)] sm:left-auto sm:top-auto sm:right-4 sm:bottom-24 sm:h-auto w-auto sm:w-[380px] max-h-none sm:max-h-[72vh] panel rounded-[6px] shadow-2xl overflow-hidden flex flex-col animate-rise">
          <div className="flex items-center justify-between px-4 py-3 border-b hairline bg-[#33559d] text-white">
            <span className="font-display font-600 text-sm inline-flex items-center gap-2"><MessagesSquare size={16} /> Richieste reparti</span>
            <div className="flex items-center gap-1">
              <button data-testid="reqchat-collapse" onClick={() => doCollapse(true)} title="Riduci a lato" className="p-1 rounded hover:bg-white/15"><ChevronLeft size={16} /></button>
              <button data-testid="reqchat-close" onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/15"><X size={16} /></button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b hairline text-sm shrink-0">
            {isRecipient && (
              <button data-testid="reqchat-tab-ricevute" onClick={() => setTab("ricevute")}
                className={`flex-1 py-2.5 border-b-2 -mb-px ${tab === "ricevute" ? "border-[#33559d] text-[#2c4a89] font-600" : "border-transparent t-muted"}`}>
                Ricevute {received.length > 0 && <span className="t-faint">({received.length})</span>}
              </button>
            )}
            <button data-testid="reqchat-tab-inviate" onClick={() => setTab("inviate")}
              className={`flex-1 py-2.5 border-b-2 -mb-px ${tab === "inviate" ? "border-[#33559d] text-[#2c4a89] font-600" : "border-transparent t-muted"}`}>
              Inviate {sent.length > 0 && <span className="t-faint">({sent.length})</span>}
            </button>
            {!isSales && (
              <button data-testid="reqchat-tab-acquisti" onClick={() => setTab("acquisti")}
                className={`flex-1 py-2.5 border-b-2 -mb-px ${tab === "acquisti" ? "border-[#33559d] text-[#2c4a89] font-600" : "border-transparent t-muted"}`}>
                Miei acquisti
              </button>
            )}
          </div>

          {/* New request button */}
          <div className="px-3 py-2.5 border-b hairline shrink-0">
            <button data-testid="reqchat-new-btn" onClick={() => setShowForm((s) => !s)}
              className="btn-primary btn-industrial w-full py-2 rounded-[3px] text-sm inline-flex items-center justify-center gap-1.5">
              <Plus size={15} /> Nuova richiesta
            </button>
          </div>

          {/* Filtri + selezione multipla */}
          {!showForm && tab !== "acquisti" && (
            <div className="px-3 py-2 border-b hairline shrink-0 space-y-2 bg-[#fafbfc]">
              <div className="flex gap-1.5">
                <button data-testid="reqchat-only-open" onClick={() => setOnlyOpen((v) => !v)}
                  className={`px-2.5 py-1 rounded-[3px] text-[11px] font-700 border inline-flex items-center gap-1 shrink-0 ${onlyOpen ? "bg-[#9a5b00] text-white border-[#9a5b00]" : "border-[#e6c98a] text-[#9a5b00] bg-[#fff7ea]"}`}>
                  <Clock size={12} /> Solo da gestire
                </button>
                <select data-testid="reqchat-filter-kind" className="field py-1 text-[12px] flex-1" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
                  <option value="">Tutti i tipi</option>
                  <option value="taglio">Pezzi da tagliare</option>
                  <option value="modifica">Modifica componenti</option>
                  <option value="richiesta">Richiedi pezzi</option>
                </select>
                <select data-testid="reqchat-filter-machine" className="field py-1 text-[12px] flex-1" value={machineFilter} onChange={(e) => setMachineFilter(e.target.value)}>
                  <option value="">Tutte le macchine</option>
                  {(meta.machine_types || []).map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              {selectable.length > 0 && (
                <div className="flex items-center gap-2 text-[11px]">
                  <label className="inline-flex items-center gap-1 font-600 t-head cursor-pointer">
                    <input type="checkbox" data-testid="reqchat-sel-all" checked={selInList.length === selectable.length && selectable.length > 0}
                      onChange={(e) => setSelected(e.target.checked ? selectable.map((it) => it.id) : [])} />
                    Tutte ({selectable.length})
                  </label>
                  <button data-testid="reqchat-bulk-complete" disabled={selInList.length === 0 || bulkBusy} onClick={bulkComplete}
                    className="ml-auto btn-industrial px-2.5 py-1 rounded-[3px] bg-[#1c7a44] text-white inline-flex items-center gap-1 disabled:opacity-50">
                    <CheckCheck size={12} /> Completa ({selInList.length})
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Form */}
          {showForm && (
            <form data-testid="reqchat-form" onSubmit={submit} className="px-3 py-3 border-b hairline bg-[#f7f9fc] space-y-2.5 shrink-0 overflow-y-auto max-h-[62vh] sm:max-h-[46vh]">
              <div>
                <label className="lbl">Tipo richiesta</label>
                <select data-testid="reqchat-kind" className="field mt-1 py-1.5 text-sm" value={form.kind}
                  onChange={(e) => { setForm({ ...emptyForm, kind: e.target.value }); setItemQuery(""); }}>
                  <option value="taglio">Pezzi da tagliare</option>
                  <option value="modifica">Modifica componenti</option>
                  <option value="richiesta">Richiedi pezzi</option>
                  {!isSales && <option value="acquisto">Richiesta di acquisto</option>}
                </select>
              </div>

              {form.kind === "acquisto" ? (
                <>
                  <div className="rounded-[3px] bg-[#eef4ff] border border-[#cddcf6] px-2.5 py-1.5 text-[11px] text-[#2c4a89]">Va in Acquisti, da approvare dalla Direzione/Amministrazione.</div>
                  <div>
                    <label className="lbl">Fonte articolo</label>
                    <div className="grid grid-cols-3 gap-1 mt-1">
                      {[["magazzino", "Magazzino"], ["materia_prima", "Mat. Prima"], ["nuovo", "Pezzo nuovo"]].map(([k, lbl]) => (
                        <button key={k} type="button" data-testid={`reqchat-source-${k}`}
                          onClick={() => { setForm2({ source: k, item_name: "", item_code: "", component_key: "", ref_id: "" }); setItemQuery(""); }}
                          className={`btn-industrial py-1.5 rounded-[3px] text-[11px] font-600 border ${form.source === k ? "bg-[#33559d] text-white border-[#33559d]" : "hairline t-muted"}`}>{lbl}</button>
                      ))}
                    </div>
                  </div>
                  {form.source === "nuovo" ? (
                    <>
                      <div>
                        <label className="lbl">Descrizione pezzo *</label>
                        <input data-testid="reqchat-new-name" className="field mt-1 py-1.5 text-sm" placeholder="Descrivi il pezzo" value={form.item_name} onChange={(e) => setForm2({ item_name: e.target.value })} />
                      </div>
                      <div>
                        <label className="lbl">Codice (opz.)</label>
                        <input data-testid="reqchat-new-code" className="field mt-1 py-1.5 text-sm" value={form.item_code} onChange={(e) => setForm2({ item_code: e.target.value })} />
                      </div>
                    </>
                  ) : (
                    <div>
                      <label className="lbl">Articolo *</label>
                      {form.item_name ? (
                        <div className="mt-1 flex items-center justify-between gap-2 rounded-[3px] border border-[#bcd0f5] bg-white px-2.5 py-1.5">
                          <div className="min-w-0"><div className="text-[13px] t-head font-600 truncate">{form.item_name}</div>{form.item_code && <div className="text-[10px] font-mono t-faint">{form.item_code}</div>}</div>
                          <button type="button" data-testid="reqchat-item-change" onClick={() => { setForm2({ item_name: "", item_code: "", component_key: "", ref_id: "" }); setItemQuery(""); }} className="text-[11px] text-[#33559d] shrink-0">Cambia</button>
                        </div>
                      ) : (
                        <>
                          <div className="relative mt-1">
                            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 t-faint" />
                            <input data-testid="reqchat-item-search" className="field pl-8 py-1.5 text-sm" placeholder="Cerca nome o codice…" value={itemQuery} onChange={(e) => setItemQuery(e.target.value)} onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: "center", behavior: "smooth" }), 250)} />
                          </div>
                          {itemQuery.trim() && (
                            <div className="mt-1 max-h-36 overflow-y-auto border hairline rounded-[3px] divide-y bg-white">
                              {pickerResults.length === 0 && <div className="px-2.5 py-1.5 text-[11px] t-faint">Nessuno — usa "Pezzo nuovo"</div>}
                              {pickerResults.map((s) => (
                                <button key={s.id || s.key} type="button" data-testid="reqchat-item-option" onClick={() => selectItem(s)} className="w-full text-left px-2.5 py-1.5 hover:bg-[#eef2fb] text-[12px]">
                                  <span className="t-head font-500">{s.name}</span>{s.code && <span className="font-mono t-faint ml-1.5 text-[10px]">{s.code}</span>}
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="lbl">Quantità *</label>
                      <input data-testid="reqchat-quantity" type="number" min="0" step="1" className="field mt-1 py-1.5 text-sm" value={form.quantity} onChange={(e) => setForm2({ quantity: e.target.value })} />
                    </div>
                    <div>
                      <label className="lbl">Prezzo € (opz.)</label>
                      <input data-testid="reqchat-price" type="number" min="0" step="0.01" className="field mt-1 py-1.5 text-sm" value={form.price} onChange={(e) => setForm2({ price: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <label className="lbl">Fornitore (opz.)</label>
                    <input data-testid="reqchat-supplier" className="field mt-1 py-1.5 text-sm" value={form.supplier} onChange={(e) => setForm2({ supplier: e.target.value })} />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="lbl">Destinatario</label>
                    <select data-testid="reqchat-recipient" className="field mt-1 py-1.5 text-sm" value={form.recipient} onChange={(e) => setForm2({ recipient: e.target.value })}>
                      {RECIPIENTS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="lbl">Tipologia macchina</label>
                    <select data-testid="reqchat-machine-type" className="field mt-1 py-1.5 text-sm" value={form.machine_type} onChange={(e) => setForm2({ machine_type: e.target.value })}>
                      <option value="">— Seleziona —</option>
                      {(meta.machine_types || []).map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className={`grid ${form.kind === "richiesta" ? "grid-cols-1" : "grid-cols-2"} gap-2`}>
                    <div>
                      <label className="lbl">Codice pezzo</label>
                      <input data-testid="reqchat-code" className="field mt-1 py-1.5 text-sm" value={form.code} onChange={(e) => setForm2({ code: e.target.value })} />
                    </div>
                    {form.kind !== "richiesta" && (
                      <div>
                        <label className="lbl">Spessore</label>
                        <input data-testid="reqchat-thickness" className="field mt-1 py-1.5 text-sm" placeholder="es. 2 mm" value={form.thickness} onChange={(e) => setForm2({ thickness: e.target.value })} />
                      </div>
                    )}
                  </div>
                  {(form.kind === "taglio" || form.kind === "richiesta") && (
                    <div className="w-28">
                      <label className="lbl">Quantità</label>
                      <input data-testid="reqchat-quantity" type="number" min="0" step="1" className="field mt-1 py-1.5 text-sm" value={form.quantity} onChange={(e) => setForm2({ quantity: e.target.value })} />
                    </div>
                  )}
                </>
              )}
              <div>
                <label className="lbl">Nota</label>
                <textarea data-testid="reqchat-note" rows={2} className="field mt-1 py-1.5 text-sm" value={form.note} onChange={(e) => setForm2({ note: e.target.value })} />
              </div>
              <div>
                <label className="lbl inline-flex items-center gap-1"><Paperclip size={11} /> Foto / allegati</label>
                <div className="mt-1"><MediaUploader testid="reqchat-media" multiple media={form.attachments} onChange={(m) => setForm2({ attachments: m })} /></div>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => { setShowForm(false); setForm(emptyForm); setItemQuery(""); }} className="btn-ghost btn-industrial flex-1 py-2 rounded-[3px] text-sm">Annulla</button>
                <button type="submit" data-testid="reqchat-send" disabled={sending} className="btn-primary btn-industrial flex-[2] py-2 rounded-[3px] text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
                  <Send size={14} /> {sending ? "Invio…" : (form.kind === "acquisto" ? "Invia ad Acquisti" : "Invia richiesta")}
                </button>
              </div>
            </form>
          )}

          {/* List */}
          <div className="flex-1 overflow-y-auto divide-y hairline">
            {tab === "acquisti" ? (
              <>
                {myRequests.length === 0 && <div className="py-10 text-center t-faint text-sm">Nessuna richiesta di acquisto inviata</div>}
                {myRequests.map((r) => {
                  const PO = {
                    richiesta: { l: "Da approvare", c: "bg-[#eef1f6] text-[#5b6b82]" },
                    da_revisionare: { l: "Da revisionare", c: "bg-[#fff4e5] text-[#9a5b00]" },
                    approvato: { l: "Approvato", c: "bg-[#eef2fb] text-[#2c4a89]" },
                    ordinato: { l: "Ordinato", c: "bg-[#e6f0ff] text-[#1d3a74]" },
                    parziale: { l: "Ricevuto parz.", c: "bg-[#e8f2e9] text-[#1c7a44]" },
                    ricevuto: { l: "Ricevuto", c: "bg-[#e8f2e9] text-[#1c7a44]" },
                    rifiutato: { l: "Rifiutato", c: "bg-[#fbeaec] text-[#9c1e2b]" },
                  }[r.status] || { l: r.status, c: "bg-[#eef1f6] text-[#5b6b82]" };
                  const it0 = (r.items || [])[0] || {};
                  return (
                    <div key={r.id} data-testid={`reqchat-myreq-${r.id}`} className="px-3.5 py-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="inline-flex items-center gap-1.5 text-[13px] font-600 t-head">
                          <ShoppingCart size={14} className="text-[#33559d]" /> {r.number}
                        </div>
                        <span className={`text-[10px] font-700 uppercase tracking-wide px-1.5 py-0.5 rounded-[3px] ${PO.c}`}>{PO.l}</span>
                      </div>
                      <div className="mt-1 text-[12px] t-body">
                        <span className="t-head font-500">{it0.name}</span>
                        {it0.code && <span className="font-mono t-faint ml-1.5 text-[11px]">{it0.code}</span>}
                        {(r.items || []).length > 1 && <span className="t-faint"> +{r.items.length - 1} altri</span>}
                      </div>
                      <div className="mt-0.5 text-[11px] t-faint">Q.tà: <span className="t-body">{it0.qty_ordered}</span>{r.supplier_name ? ` · ${r.supplier_name}` : ""}</div>
                      <div className="mt-1 flex items-center justify-between text-[10px] t-faint font-mono">
                        <span>{timeAgo(r.created_at)}</span>
                        {r.last_receive && <span className="text-[#1c7a44]">Ricevuto il {String(r.last_receive).slice(0, 10)}</span>}
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <>
            {list.length === 0 && <div className="py-10 text-center t-faint text-sm">Nessuna richiesta</div>}
            {list.map((it) => {
              const Icon = it.kind === "taglio" ? Scissors : it.kind === "richiesta" ? Package : Wrench;
              const kindLabel = it.kind === "taglio" ? "Pezzi da tagliare" : it.kind === "richiesta" ? "Richiedi pezzi" : "Modifica componenti";
              const kindColor = it.kind === "taglio" ? "text-[#b45309]" : it.kind === "richiesta" ? "text-[#1c7a44]" : "text-[#33559d]";
              const canHandle = tab === "ricevute" && ["inviata", "visualizzata"].includes(it.status);
              const mine = it.created_by_id === myId;
              return (
                <div key={it.id} data-testid={`req-card-${it.id}`} className={`px-3.5 py-3 ${selected.includes(it.id) ? "bg-[#eef4ff]" : ""}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="inline-flex items-center gap-1.5 text-[13px] font-600 t-head">
                      {canHandle && (
                        <input type="checkbox" data-testid={`reqchat-sel-${it.id}`} checked={selected.includes(it.id)} onChange={() => toggleSel(it.id)} className="mr-0.5" />
                      )}
                      <Icon size={14} className={kindColor} />
                      {kindLabel}
                    </div>
                    <StatusBadge status={it.status} />
                  </div>
                  <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[12px] t-body">
                    {it.machine_type && <div><span className="t-faint">Macchina:</span> <span className="t-head">{it.machine_type}</span></div>}
                    {it.code && <div><span className="t-faint">Codice:</span> <span className="font-mono t-head">{it.code}</span></div>}
                    {(it.kind === "taglio" || it.kind === "richiesta") && <div><span className="t-faint">Q.tà:</span> <span className="t-head">{it.quantity}</span></div>}
                    {it.thickness && <div><span className="t-faint">Spessore:</span> <span className="t-head">{it.thickness}</span></div>}
                  </div>
                  {it.note && <div className="mt-1 text-[12px] t-body bg-[#f7f9fc] rounded-[3px] px-2 py-1.5 break-words">{it.note}</div>}
                  {(it.attachments || []).length > 0 && (
                    <div className="mt-1.5"><MediaUploader testid={`reqchat-att-${it.id}`} media={it.attachments} onChange={() => {}} readOnly /></div>
                  )}
                  <div className="mt-1.5 flex items-center justify-between text-[10px] t-faint font-mono">
                    <span>{tab === "ricevute" ? `da ${it.created_by_name}` : `a ${recLabel(it.recipient)}`} · {timeAgo(it.created_at)}</span>
                    {(it.status === "visualizzata" || it.status === "accettata" || it.status === "declinata") && (
                      <span data-testid={`req-seen-${it.id}`} className="inline-flex items-center gap-0.5 text-[#2c4a89]"><CheckCheck size={12} /> Visualizzata</span>
                    )}
                  </div>
                  {it.response_note && (
                    <div className={`mt-1.5 text-[12px] rounded-[3px] px-2 py-1.5 ${it.status === "declinata" ? "bg-[#fbeaec] text-[#9c1e2b]" : "bg-[#e8f2e9] text-[#1c7a44]"}`}>
                      <span className="font-600">Risposta:</span> {it.response_note}
                    </div>
                  )}

                  {canHandle && declineFor !== it.id && (
                    <div className="mt-2 flex gap-2">
                      <button data-testid={`req-accept-${it.id}`} onClick={() => respond(it.id, "completata")} className="btn-industrial flex-1 py-1.5 rounded-[3px] text-xs bg-[#1c7a44] text-white inline-flex items-center justify-center gap-1"><CheckCheck size={13} /> Completa</button>
                      <button data-testid={`req-decline-${it.id}`} onClick={() => { setDeclineFor(it.id); setDeclineNote(""); }} className="btn-industrial flex-1 py-1.5 rounded-[3px] text-xs bg-[#9c1e2b] text-white inline-flex items-center justify-center gap-1"><Ban size={13} /> Declina</button>
                    </div>
                  )}
                  {canHandle && declineFor === it.id && (
                    <div className="mt-2 space-y-1.5">
                      <input data-testid={`req-decline-note-${it.id}`} className="field py-1.5 text-sm" placeholder="Motivo del rifiuto (opzionale)" value={declineNote} onChange={(e) => setDeclineNote(e.target.value)} />
                      <div className="flex gap-2">
                        <button onClick={() => setDeclineFor(null)} className="btn-ghost btn-industrial flex-1 py-1.5 rounded-[3px] text-xs">Annulla</button>
                        <button data-testid={`req-decline-confirm-${it.id}`} onClick={() => respond(it.id, "declinata", declineNote)} className="btn-industrial flex-[2] py-1.5 rounded-[3px] text-xs bg-[#9c1e2b] text-white">Conferma rifiuto</button>
                      </div>
                    </div>
                  )}
                  {mine && ["inviata", "visualizzata"].includes(it.status) && (
                    <button data-testid={`req-del-${it.id}`} onClick={() => remove(it.id)} className="mt-1.5 text-[11px] t-faint hover:text-[#9c1e2b] inline-flex items-center gap-1"><Trash2 size={12} /> Elimina</button>
                  )}
                </div>
              );
            })}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
